import type { GoodsReceiptResponse } from '@palaccount/contracts';
import { CheckCheck, Pencil, Receipt, RotateCcw, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { api } from '@/api';
import { Amount, Qty } from '@/components/amount';
import {
  BulkExportButton,
  BulkPdfButton,
  BulkPostButton,
  DocumentListPage,
  DocumentPdfButton,
  documentLocale,
  normalisePaged,
  type DocumentListConfig,
} from '@/components/document-list';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { denyPermission } from '@/lib/permissions';
import { ApiError, localizeServerCause, throwApiError } from '@/lib/server-messages';
import { useBaseCurrency } from '@/lib/session';

import {
  BILLS_KEY,
  fetchGoodsReceiptsPage,
  GRN_KEY,
  PURCHASE_ORDERS_KEY,
  useCancelGoodsReceipt,
  useGoodsReceipt,
  useGoodsReceipts,
  useGoodsReceiptStats,
  usePostGoodsReceipt,
} from './api';
import { receiptIsLate } from './grn-rules';
import { goodsReceiptSteps } from './purchase-steppers';
import { PurchasesTabs } from './purchases-tabs';

const STATUSES: GoodsReceiptResponse['status'][] = ['draft', 'posted', 'cancelled'];
const TONES = { draft: 'draft', posted: 'posted', cancelled: 'cancelled' } as const;

type GoodsReceiptStats = ReturnType<typeof useGoodsReceiptStats>['data'] & object;

const trimQty = (qty: string) => qty.replace(/\.?0+$/, '');

/**
 * PUR-02's list on the shared list-and-detail page (wave 3B). The accepted
 * value is a stock valuation in the company's base currency (GEN-107, from
 * the session — nothing here converts); rejected goods never reach it
 * (BR-PUR-02-03). BR-PUR-02-11's late flag stays on the LIST, because "so
 * late suppliers can be found" is a scanning job. The selected receipt sits
 * at Receipt in the purchasing chain with its order linked, says how much
 * of what arrived was accepted, and offers the acts: post a draft, print,
 * raise the bill on the order once posted, send rejected goods back, cancel
 * (BR-PUR-02-08, reason required — GEN-72).
 */
export function ReceiptsListPage() {
  const { t, i18n } = useTranslation();
  const baseCurrency = useBaseCurrency();
  const locale = documentLocale(i18n.language);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelAsk, setCancelAsk] = useState(false);
  const [params] = useSearchParams();
  const selectedId = params.get('selected') ?? '';
  const postReceipt = usePostGoodsReceipt(selectedId);
  const cancelReceipt = useCancelGoodsReceipt(selectedId);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('purchasing.approve');
      setError(localizeServerCause(t, thrown, 'doclist.actFailed'));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (status: GoodsReceiptResponse['status']) => t(`common.status.${status}`);
  const chip = (receipt: GoodsReceiptResponse) => (
    <span className="inline-flex gap-1">
      <StatusChip status={receipt.status} tones={TONES} ns="common" />
      {receiptIsLate(receipt) ? (
        <span
          className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
          title={t('grn.lateTitle', { expected: receipt.expectedDate, received: receipt.receiptDate })}
        >
          {t('grn.lateChip')}
        </span>
      ) : null}
    </span>
  );
  const exportColumns = [
    { header: t('grn.colNo'), value: (r: GoodsReceiptResponse) => r.code },
    { header: t('common.date'), value: (r: GoodsReceiptResponse) => r.receiptDate, kind: 'date' as const },
    { header: t('po.colSupplier'), value: (r: GoodsReceiptResponse) => r.partnerName },
    { header: t('bills.colOrder'), value: (r: GoodsReceiptResponse) => r.purchaseOrderCode },
    { header: t('grn.colReceived'), value: (r: GoodsReceiptResponse) => r.totalReceived, kind: 'number' as const },
    { header: t('grn.colAccepted'), value: (r: GoodsReceiptResponse) => r.totalAccepted, kind: 'number' as const },
    { header: t('grn.colValue'), value: (r: GoodsReceiptResponse) => r.acceptedValue, kind: 'number' as const },
    { header: t('sales.colStatus'), value: (r: GoodsReceiptResponse) => statusLabel(r.status) },
  ];
  const pdfRequest = (receipt: GoodsReceiptResponse) => ({ kind: 'goods_receipt' as const, locale, subjectId: receipt.id });
  const toFilters = (filters: { status: GoodsReceiptResponse['status'] | ''; q: string; dateFrom: string; dateTo: string }) => ({
    status: filters.status || undefined,
    q: filters.q || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
  });

  const config: DocumentListConfig<GoodsReceiptResponse, GoodsReceiptResponse['status'], GoodsReceiptStats> = {
    titleKey: 'grn.title',
    tabs: <PurchasesTabs />,
    newAction: { to: '/purchases/receipts/new', labelKey: 'common.new', permission: 'purchasing.create', reasonKey: 'perm.cannot.createReceipts' },
    statuses: STATUSES,
    statusLabel,
    search: { labelKey: 'grn.searchLabel', placeholderKey: 'grn.searchPlaceholder' },
    dateRange: true,
    useList: (filters, page, pageSize) => {
      const query = useGoodsReceipts(toFilters(filters), page, pageSize);
      return { data: normalisePaged(query.data, 'receipts'), isPending: query.isPending, isError: query.isError };
    },
    useById: (id) => useGoodsReceipt(id ?? undefined),
    kpis: {
      useStats: () => {
        const stats = useGoodsReceiptStats();
        return { data: stats.data, isPending: stats.isPending, isError: stats.isError };
      },
      tiles: [
        { key: 'drafts', labelKey: 'grn.tileDrafts', format: 'count', value: (s) => s.draftsToPost },
        { key: 'week', labelKey: 'grn.tileThisWeek', format: 'count', value: (s) => s.receivedThisWeek, tone: 'pos' },
        { key: 'awaiting', labelKey: 'grn.tileAwaiting', format: 'count', value: (s) => s.ordersAwaitingReceipt, tone: 'warn' },
        { key: 'rejected', labelKey: 'grn.tileRejected', format: 'count', value: (s) => trimQty(s.rejectedQty), tone: 'neg' },
      ],
    },
    card: (receipt) => ({
      code: receipt.code,
      chip: chip(receipt),
      title: receipt.partnerName,
      meta: (
        <bdi dir="ltr">
          {receipt.receiptDate} · {trimQty(receipt.totalAccepted)} / {trimQty(receipt.totalReceived)}
        </bdi>
      ),
      amount: { value: receipt.acceptedValue, currency: baseCurrency },
    }),
    columns: { titleKey: 'po.colSupplier', metaKey: 'grn.colReceived', amountKey: 'grn.colValue', statusKey: 'sales.colStatus' },
    emptyKey: 'grn.empty',
    selectOneKey: 'grn.selectOne',
    notice: error ? (
      <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    ) : null,
    export: {
      filename: 'goods-receipts',
      columns: exportColumns,
      fetchPage: (filters, page, pageSize) => fetchGoodsReceiptsPage(toFilters(filters), page, pageSize),
    },
    bulkActions: (rows, clear) => (
      <>
        <BulkPostButton
          rows={rows}
          permission="purchasing.approve"
          reasonKey="perm.cannot.postReceipts"
          invalidate={[GRN_KEY, PURCHASE_ORDERS_KEY, BILLS_KEY]}
          clear={clear}
          post={async (row) => {
            const response = await api.goodsReceipts.post({ params: { receiptId: row.id }, body: {} });
            if (response.status !== 200) throwApiError(response.status, response.body);
          }}
        />
        <BulkPdfButton requests={rows.map(pdfRequest)} />
        <BulkExportButton filename="goods-receipts" columns={exportColumns} rows={rows} />
      </>
    ),
    detail: {
      chip,
      links: (receipt) => [
        {
          key: 'order',
          to: `/purchases/orders?selected=${receipt.purchaseOrderId}`,
          tone: 'muted',
          label: t('bills.fromOrder', { code: receipt.purchaseOrderCode }),
        },
      ],
      subline: (receipt) => (
        <>
          {receipt.partnerName} · {receipt.warehouseName} · <bdi dir="ltr">{receipt.receiptDate}</bdi>
        </>
      ),
      steps: { labelKey: 'po.stepper', of: goodsReceiptSteps },
      /* "accepted / ordered" — what went onto the shelf against what the order asked for. */
      progress: (receipt) =>
        t('grn.listProgress', { accepted: trimQty(receipt.totalAccepted), ordered: trimQty(receipt.totalOrdered) }),
      actions: (receipt) => (
        <>
          <Button asChild variant="outline">
            <Link to={`/purchases/receipts/${receipt.id}`}>
              <Pencil aria-hidden /> {receipt.status === 'draft' ? t('common.edit') : t('doclist.open')}
            </Link>
          </Button>
          {receipt.status === 'draft' ? (
            <Gated permission="purchasing.approve" reasonKey="perm.cannot.postReceipts">
              <Button onClick={() => void act(() => postReceipt.mutateAsync())} disabled={busy}>
                <CheckCheck aria-hidden /> {t('grn.actPost')}
              </Button>
            </Gated>
          ) : null}
          <DocumentPdfButton request={pdfRequest(receipt)} />
          {receipt.status === 'posted' ? (
            <Gated permission="purchasing.create" reasonKey="perm.cannot.createBills">
              <Button asChild variant="outline">
                <Link to={`/purchases/bills/new?poId=${receipt.purchaseOrderId}`}>
                  <Receipt aria-hidden /> {t('grn.actBill')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          {receipt.status === 'posted' && receipt.rejectedLineCount > 0 ? (
            <Gated permission="purchasing.create" reasonKey="perm.cannot.createPurchaseReturns">
              <Button asChild variant="outline">
                <Link to="/purchases/returns/new">
                  <RotateCcw aria-hidden /> {t('grn.actReturnRejected')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          {receipt.status === 'posted' ? (
            <Gated permission="purchasing.approve" reasonKey="perm.cannot.cancelReceipts">
              <Button variant="destructive" onClick={() => setCancelAsk(true)} disabled={busy}>
                <XCircle aria-hidden /> {t('grn.actCancel')}
              </Button>
            </Gated>
          ) : null}
          {receipt.status === 'posted' ? (
            <ReasonBox
              open={cancelAsk}
              title={t('grn.listCancelTitle')}
              prompt={t('grn.listCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => {
                setCancelAsk(false);
                void act(() => cancelReceipt.mutateAsync(reason));
              }}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
        </>
      ),
      lines: {
        columns: [
          { key: 'item', labelKey: 'quotes.colItem' },
          { key: 'ordered', labelKey: 'dn.colOrdered', align: 'end', className: 'w-28' },
          { key: 'received', labelKey: 'grn.colReceived', align: 'end', className: 'w-28' },
          { key: 'accepted', labelKey: 'grn.colAccepted', align: 'end', className: 'w-28' },
          { key: 'value', labelKey: 'grn.colValue', align: 'end', className: 'w-32' },
        ],
        rows: (receipt) =>
          receipt.lines.map((line) => ({
            key: String(line.lineNo),
            cells: [
              <span key="d" className="font-medium">{line.description}</span>,
              <Qty key="o" value={line.qtyOrdered} unit={line.unitSymbol} precision={line.unitPrecision} />,
              <Qty key="r" value={line.qtyReceived} unit={line.unitSymbol} precision={line.unitPrecision} />,
              <Qty key="a" value={line.acceptedQty} unit={line.unitSymbol} precision={line.unitPrecision} />,
              <Amount key="v" value={line.lineValue} currency={baseCurrency} />,
            ],
          })),
      },
    },
  };

  return <DocumentListPage config={config} />;
}
